// Microbe Sandbox — bot-vs-bot GOD VIEW.
//
// A thin wrapper that PULLS the live microbe-rts sim + renderer dynamically (relative imports into
// ../microbe-rts, not a copy) and adds only: the two-bot toggle (createSim {bothBots}), the god-view
// flag (mountRender {godView}), watch controls (pause + 1×/4×/16× + rematch), and a live scoreboard.
// No input / hud / evolve is mounted — this is watch-only, so there's no human-command conflict.
import { createLoop } from '../src/index.js';
import { createSim } from '../microbe-rts/sim/sim.js';
import { mountRender } from '../microbe-rts/render/render.js';
import { WORLD, BOT_PROFILES, MUTATIONS, CAMERA } from '../microbe-rts/config.js';
import { iconSvg } from '../microbe-rts/ui/icons.js';
import type { Side } from '../microbe-rts/types.js';

const canvas = document.getElementById('stage') as HTMLCanvasElement | null;
const scoreEl = document.getElementById('scoreboard');
if (!canvas || !scoreEl) throw new Error('microbe-sandbox: missing #stage / #scoreboard');

const params = new URLSearchParams(location.search);

// ---- profile matchup: pick a named, plug-and-play profile per side (?p=…&pYou=…) ----
// The registry (BOT_PROFILES) is shared with the real game, so whatever wins here is selectable
// in-game by the same key. The 'you' side is TEAL, 'bot' is RED (matches the scoreboard colours).
const names = Object.keys(BOT_PROFILES);
const pick = (key: string, fallback: string) => {
  const v = params.get(key);
  return v && BOT_PROFILES[v] ? v : fallback;
};
const tealName = pick('pYou', 'reef');   // TEAL === 'you' side
const redName = pick('p', 'expansionist'); // RED === 'bot' side; showcase the land-grabber by default

function fillPicker(id: string, selected: string): void {
  const el = document.getElementById(id) as HTMLSelectElement | null;
  if (!el) return;
  el.innerHTML = names.map((n) => `<option value="${n}"${n === selected ? ' selected' : ''}>${n}</option>`).join('');
  el.addEventListener('change', () => {
    // TEAL drives ?pYou, RED drives ?p — reload into a fresh match with the new matchup.
    if (id === 'pTeal') params.set('pYou', el.value); else params.set('p', el.value);
    location.search = params.toString();
  });
}
fillPicker('pTeal', tealName);
fillPicker('pRed', redName);

// Fresh match each load; ?seed=N pins one for reproducible watching.
const urlSeed = Number(params.get('seed'));
const seed = (Number.isFinite(urlSeed) && urlSeed > 0 ? urlSeed : (Date.now() & 0xffff)) || 1;
const sim = createSim({
  bothBots: true,
  seed,
  profile: BOT_PROFILES[redName],    // RED / 'bot' side
  profileYou: BOT_PROFILES[tealName], // TEAL / 'you' side
});
const { render, cam } = mountRender(canvas, sim, { godView: true });

// God-view perk: let the sandbox pull back ~3× further than the in-game floor (0.35) so you can
// watch the whole battlefield from way out. This mutates CAMERA in the sandbox bundle ONLY — the
// shipped game imports its own copy of config, so its zoom clamp is untouched.
CAMERA.zoomMin = CAMERA.zoomMin / 3; // 0.35 -> ~0.117

// ---- free camera: watch-only pan + zoom (no unit commands) ----
// The renderer drives cam.update() each frame; we only push pan/zoom intent. touch-action:none
// stops the browser from stealing the gesture (page scroll / pinch-zoom).
const cv: HTMLCanvasElement = canvas; // non-null alias (survives closure narrowing)
cv.style.touchAction = 'none';
function refitWholeMap(): void {
  cam.jumpTo({ x: WORLD.width / 2, y: WORLD.height / 2 });
  cam.zoom = 0; // clamped to CAMERA.zoomMin -> whole-map overview
}
const pointers = new Map<number, { x: number; y: number }>();
let pinchDist = 0;
const localXY = (e: PointerEvent) => {
  const r = cv.getBoundingClientRect();
  return { x: e.clientX - r.left, y: e.clientY - r.top };
};
const centroid = () => {
  let x = 0, y = 0;
  for (const p of pointers.values()) { x += p.x; y += p.y; }
  const n = pointers.size || 1;
  return { x: x / n, y: y / n };
};
const spread = () => {
  const pts = [...pointers.values()];
  if (pts.length < 2) return 0;
  return Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
};

// double-tap / double-click to refit the whole battlefield
let lastTap = 0;
let lastTapAt = { x: 0, y: 0 };

cv.addEventListener('pointerdown', (e) => {
  cv.setPointerCapture(e.pointerId);
  pointers.set(e.pointerId, localXY(e));
  if (pointers.size === 2) pinchDist = spread();
});
cv.addEventListener('pointermove', (e) => {
  const prev = pointers.get(e.pointerId);
  if (!prev) return;
  const cur = localXY(e);
  if (pointers.size === 1) {
    cam.pan(cur.x - prev.x, cur.y - prev.y);
    pointers.set(e.pointerId, cur);
  } else if (pointers.size === 2) {
    const beforeC = centroid();
    pointers.set(e.pointerId, cur);
    const afterC = centroid();
    cam.pan(afterC.x - beforeC.x, afterC.y - beforeC.y); // two-finger drag
    const d = spread();
    if (pinchDist > 0 && d > 0) cam.pinchZoom(d / pinchDist, afterC); // pinch to zoom
    pinchDist = d;
  }
});
function endPointer(e: PointerEvent): void {
  pointers.delete(e.pointerId);
  if (pointers.size < 2) pinchDist = 0;
  try { cv.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
}
cv.addEventListener('pointerup', (e) => {
  // tap (no meaningful drag) -> detect double-tap to refit
  const t = performance.now();
  const p = localXY(e);
  if (t - lastTap < 300 && Math.hypot(p.x - lastTapAt.x, p.y - lastTapAt.y) < 24) {
    refitWholeMap();
    lastTap = 0;
  } else {
    lastTap = t;
    lastTapAt = p;
  }
  endPointer(e);
});
cv.addEventListener('pointercancel', endPointer);
cv.addEventListener('wheel', (e) => {
  e.preventDefault();
  const p = localXY(e as unknown as PointerEvent);
  cam.zoomAt(p.x, p.y, e.deltaY < 0 ? 1.1 : 1 / 1.1);
}, { passive: false });

// ---- watch controls ----
let speed = 1;
let paused = false;
let treeOpen = false;
const wire = (id: string, fn: () => void) => document.getElementById(id)?.addEventListener('click', fn);
wire('pp', () => { paused = !paused; syncControls(); });
for (const s of [1, 4, 16]) wire('sp' + s, () => { speed = s; paused = false; syncControls(); });
wire('fit', () => refitWholeMap());
wire('rematch', () => location.reload());
// Skill-tree panel: opening it pauses the match so you can study the choices; resume (▶ / a speed) to
// watch the bots' offered/owned mutations update live. Close via ✕ (delegated) or the 🧬 button again.
wire('tree', () => { treeOpen = !treeOpen; if (treeOpen) paused = true; syncControls(); renderTree(); });
document.getElementById('tree-panel')?.addEventListener('click', (e) => {
  if ((e.target as HTMLElement).closest('#tree-x')) { treeOpen = false; syncControls(); }
});
function syncControls(): void {
  const pp = document.getElementById('pp');
  if (pp) pp.textContent = paused ? '▶' : '⏸';
  for (const s of [1, 4, 16]) document.getElementById('sp' + s)?.classList.toggle('on', !paused && speed === s);
  document.getElementById('tree')?.classList.toggle('on', treeOpen);
  const tp = document.getElementById('tree-panel');
  if (tp) tp.style.display = treeOpen ? 'block' : 'none';
}

// ---- skill-tree panel: per-side evolution state (owned / offered-now / locked) ----
const TIERS = [1, 2, 3, 4];
const mutName = (id: string) => MUTATIONS.find((m) => m.id === id)?.name ?? id;
function costHtml(c: { energy: number; amino: number; toxin: number }): string {
  const bits = [`${iconSvg('energy')}${c.energy}`, `${iconSvg('amino')}${c.amino}`];
  if (c.toxin > 0) bits.push(`${iconSvg('toxin')}${c.toxin}`);
  return bits.join(' ');
}
function badge(side: Side, m: (typeof MUTATIONS)[number], offered: Set<string>): string {
  const c = sim.state.colonies[side];
  const lvl = c.genome[m.id] ?? 0;
  let cls = 'lock', txt = '·';
  if (lvl > 0) { cls = 'have'; txt = m.maxLevel > 1 ? `${lvl}/${m.maxLevel}` : '✓'; }
  else if (offered.has(m.id)) { cls = 'offer'; txt = '◦'; }
  return `<span class="bg ${cls}" style="--c:${COLOR[side]}">${txt}</span>`;
}
function renderTree(): void {
  const panel = document.getElementById('tree-panel');
  if (!panel || !treeOpen) return;
  const off: Record<Side, Set<string>> = {
    you: new Set(sim.offeredMutations('you')),
    bot: new Set(sim.offeredMutations('bot')),
  };
  const head = (side: Side, label: string) => {
    const c = sim.state.colonies[side];
    const ks = c.keystones.map(mutName).join(', ') || '—';
    return `<div class="th" style="--c:${COLOR[side]}"><b>${label}</b> · T${c.tier} · pop ${c.popUsed}/${c.popCap}` +
      `<br><span class="ks">keystone: ${ks}</span></div>`;
  };
  let html = `<div class="tt"><b>Evolution tree</b>` +
    `<span class="hint">◦ choosing now · ✓/n owned · · locked</span>` +
    `<button id="tree-x" aria-label="close">✕</button></div>`;
  html += `<div class="theads">${head('you', 'TEAL')}${head('bot', 'RED')}</div>`;
  for (const tier of TIERS) {
    const muts = MUTATIONS.filter((m) => m.tier === tier);
    if (!muts.length) continue;
    html += `<div class="tsec">Tier ${tier}</div>`;
    for (const m of muts) {
      const kind = m.type === 'speciation' ? ' spec' : (m.keystone ? ' key' : '');
      html += `<div class="mut${kind}">` +
        `<span class="mi">${m.icon}</span><span class="mn">${m.name}</span>` +
        `<span class="mc">${costHtml(m.cost)}</span>` +
        badge('you', m, off.you) + badge('bot', m, off.bot) + `</div>`;
    }
  }
  panel.innerHTML = html;
}

// ---- scoreboard (all fields are symmetric — read straight off sim.state) ----
const COLOR: Record<Side, string> = { you: '#4fd1b0', bot: '#ff6a5a' };
function stats(side: Side) {
  const st = sim.state;
  const c = st.colonies[side];
  return {
    units: st.units.filter((u) => u.side === side && u.hp > 0).length,
    nodes: st.nodes.filter((n) => n.owner === side).length,
    tier: c.tier,
    energy: Math.round(c.res.energy),
    amino: Math.round(c.res.amino),
    toxin: Math.round(c.res.toxin),
  };
}
function renderScore(): void {
  const st = sim.state;
  const t = Math.floor(st.clocks.sim);
  const clk = `${String((t / 60) | 0).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
  const row = (side: Side, label: string) => {
    const s = stats(side);
    return `<div class="row" style="--c:${COLOR[side]}"><b>${label}</b>` +
      `<span>${s.units}✱</span><span>${s.nodes}◆</span><span>T${s.tier}</span>` +
      `<span class="res">${iconSvg('energy')}${s.energy}</span>` +
      `<span class="res">${iconSvg('amino')}${s.amino}</span>` +
      `<span class="res">${iconSvg('toxin')}${s.toxin}</span></div>`;
  };
  const over = st.result.over
    ? `<div class="winner">${st.result.winner === 'you' ? 'TEAL' : 'RED'} wins · ${st.result.reason}</div>`
    : '';
  scoreEl!.innerHTML =
    `<div class="clock">${clk} · ${paused ? 'paused' : speed + '×'}</div>` +
    row('you', 'TEAL') + row('bot', 'RED') + over;
}

// ---- fixed-step loop; speed = sim sub-steps per rendered tick ----
let lastUi = 0;
const loop = createLoop({
  step: 1 / 30,
  update: (dt) => { if (paused) return; for (let i = 0; i < speed; i++) sim.update(dt); },
  render: (alpha) => {
    render(alpha);
    const now = performance.now();
    if (now - lastUi >= 100) { lastUi = now; renderScore(); if (treeOpen) renderTree(); }
  },
});
syncControls();
renderScore();
loop.start();
